/**
 * NeighborsIndex.ts — Índice de términos vecinos (VECMUN-b).
 *
 * Consume `{baseUrl}/boundaries/vecinos.json` (VECMUN-a, snapshot DERA G13, EPSG:4326):
 *   { esquema, municipios: { INE: { n, e: [lon, lat], v: [[INE, lon, lat], …], x? } } }
 *     n  nombre del municipio       e  punto donde escribir su nombre
 *     v  vecinos, con el punto donde escribir el nombre de cada uno junto a la linde común
 *     x  km de linde sin vecino andaluz (costa, otra comunidad, Portugal, Gibraltar)
 *
 * Una sola descarga por sesión, caché en memoria y fallo recordado (no se reintenta en
 * bucle). Sin índice el mapa sigue con el término propio, pero el fallo no se calla
 * (HX-FALLBACK-ENMASCARA-BUG): `console.warn` (nunca `console.error`, que recogen los E2E)
 * y estado consultable con `getNeighborsIndexStatus()`.
 *
 * Los contornos de los vecinos NO pasan por aquí: se piden con `loadBoundaryByINE`
 * (misma caché y mismos reintentos). Este fichero no toca `MunicipalBoundaryService`.
 *
 * @version 1.0.0
 */

import { fetchWithBackoff } from '../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

export interface NeighborsIndexConfig {
  /** URL base del Worker geodata (default: producción R2, la misma que `MunicipalBoundaryService`). */
  baseUrl?: string;
  /** Timeout por intento en ms (default: 10000). */
  timeout?: number;
}

/** Entrada cruda de `vecinos.json` (esquema 1.0.0). */
interface EntradaRaw {
  n: string;
  e: [number, number];
  v: Array<[string, number, number]>;
  x?: number;
}
interface IndiceRaw {
  esquema?: string;
  municipios: Record<string, EntradaRaw>;
}

/** Vecino con el punto (lng, lat) donde escribir su nombre, junto a la linde común. */
export interface VecinoPunto {
  ine: string;
  nombre: string;
  lng: number;
  lat: number;
}

/** Un municipio y sus vecinos. (lng, lat) es el punto donde escribir el nombre del propio. */
export interface VecinosDe {
  ine: string;
  nombre: string;
  lng: number;
  lat: number;
  vecinos: VecinoPunto[];
  /** km de linde sin vecino andaluz. Anotado y sin consumir (DOP-7): queda para VECMUN-c y el informe. */
  kmSinVecino?: number;
}

export type NeighborsIndexStatus = 'cargado' | 'sin_indice' | 'error_red' | 'no_pedido';

// Fuente única del dominio del Worker (guarda de `r2Inventory.guard.test.ts`): no se escribe a mano.
const DEFAULT_BASE_URL = WORKER_BASE;
const DEFAULT_TIMEOUT_MS = 10000;

let indice: IndiceRaw | null = null;
let fallo: 'sin_indice' | 'error_red' | null = null;
let pendiente: Promise<boolean> | null = null;

function registrarFallo(motivo: 'sin_indice' | 'error_red', detalle: string): false {
  fallo = motivo;
  console.warn(`[VECMUN] Índice de términos vecinos no disponible (${detalle}): el mapa sigue sin vecinos.`);
  return false;
}

async function descargar(config: NeighborsIndexConfig): Promise<boolean> {
  const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
  const timeoutMs = config.timeout ?? DEFAULT_TIMEOUT_MS;
  try {
    const { response } = await fetchWithBackoff(`${baseUrl}/boundaries/vecinos.json`, { timeoutMs });
    if (!response.ok) {
      return registrarFallo(response.status >= 500 ? 'error_red' : 'sin_indice', `HTTP ${response.status}`);
    }
    const json = (await response.json()) as Partial<IndiceRaw> | null;
    const municipios = json?.municipios;
    if (!municipios || typeof municipios !== 'object') {
      return registrarFallo('sin_indice', 'cuerpo sin «municipios»');
    }
    indice = { esquema: json?.esquema, municipios };
    return true;
  } catch {
    // Timeout interno o red agotada tras los reintentos.
    return registrarFallo('error_red', 'fallo de red');
  }
}

/**
 * Descarga el índice una sola vez (idempotente; las llamadas concurrentes comparten la petición).
 * @returns true si el índice está cargado; false si falló (ya avisado con `console.warn`).
 */
export async function loadNeighborsIndex(config: NeighborsIndexConfig = {}): Promise<boolean> {
  if (indice) return true;
  if (fallo) return false;
  if (pendiente) return pendiente;
  pendiente = descargar(config).finally(() => { pendiente = null; });
  return pendiente;
}

const esPunto = (a: unknown): a is [number, number] =>
  Array.isArray(a) && a.length >= 2 && Number.isFinite(a[0]) && Number.isFinite(a[1]);

const tiene = (o: object, clave: string): boolean => Object.prototype.hasOwnProperty.call(o, clave);

/**
 * Vecinos de un municipio, o null si el índice no está cargado, el INE no figura en él o su
 * entrada está mal formada (un punto sin coordenadas haría lanzar a `L.Marker`). Una entrada de
 * vecino mal formada, o sin nombre, se omite sin más: no hay qué pintar.
 */
export function getNeighborsOf(ine: string): VecinosDe | null {
  if (!indice || !tiene(indice.municipios, ine)) return null;
  const m = indice.municipios[ine];
  if (!m || typeof m.n !== 'string' || !esPunto(m.e) || !Array.isArray(m.v)) return null;
  const vecinos: VecinoPunto[] = [];
  for (const item of m.v as unknown[]) {
    if (!Array.isArray(item)) continue;
    const [vIne, lng, lat] = item as unknown[];
    if (typeof vIne !== 'string' || !esPunto([lng, lat]) || !tiene(indice.municipios, vIne)) continue;
    const nombre = indice.municipios[vIne]?.n;
    if (typeof nombre !== 'string' || !nombre) continue; // sin nombre no hay qué escribir
    vecinos.push({ ine: vIne, nombre, lng: lng as number, lat: lat as number });
  }
  return {
    ine,
    nombre: m.n,
    lng: m.e[0],
    lat: m.e[1],
    vecinos,
    ...(typeof m.x === 'number' ? { kmSinVecino: m.x } : {}),
  };
}

/** Estado de la carga del índice: 'no_pedido' = ni cargado ni fallido en esta sesión. */
export function getNeighborsIndexStatus(): NeighborsIndexStatus {
  if (indice) return 'cargado';
  return fallo ?? 'no_pedido';
}

/** Vacía la caché y el fallo recordado (para pruebas). */
export function clearNeighborsIndex(): void {
  indice = null;
  fallo = null;
  pendiente = null;
}
